import { Outlet, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { booksAPI } from '../lib/api';
import {
  HomeIcon,
  BookOpenIcon,
  PlusCircleIcon,
  BookmarkIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  QuestionMarkCircleIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Library', href: '/library', icon: BookOpenIcon },
  { name: 'Add Book', href: '/add-book', icon: PlusCircleIcon },
  { name: 'My Books', href: '/my-books', icon: BookmarkIcon },
  { name: 'My Reviews', href: '/reviewing', icon: ClipboardDocumentListIcon },
  { name: 'Advertise', href: '/advertise', icon: MegaphoneIcon },
  { name: 'Affiliate', href: '/affiliate', icon: UserPlusIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
  { name: 'Help & FAQ', href: '/faq', icon: QuestionMarkCircleIcon },
];

export default function Layout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch pending count for admin badge
  const isModOrAdmin = user?.role === 'admin' || user?.role === 'moderator';
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['pending-books-count'],
    queryFn: booksAPI.getPendingCount,
    enabled: isModOrAdmin,
    refetchInterval: 60000, // Refresh every minute
  });

  const handleLogout = () => {
    logout();
    // Navigate to home page after logout
    window.location.href = '/';
  };

  // Guest Layout - for unauthenticated users
  if (!user) {
    return (
      <div className="min-h-screen bg-dark-950 bg-book-pattern">
        {/* Guest Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-dark-900/95 backdrop-blur-sm border-b border-dark-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-3">
                <img src="/favicon.png" alt="GetBookReviews" className="w-8 h-8" />
                <span className="text-lg font-serif font-bold gradient-text">
                  GetBookReviews
                </span>
              </Link>
              <div className="flex items-center gap-3">
                <Link
                  to="/library"
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    location.pathname === '/library'
                      ? 'text-primary-400'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Library
                </Link>
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary btn-sm">
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-16">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  // Authenticated Layout

  return (
    <div className="min-h-screen bg-dark-950 bg-book-pattern">
      {/* Mobile Header Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-dark-900/95 backdrop-blur-sm border-b border-dark-700">
        <div className="flex items-center justify-between px-6 h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src="/favicon.png" alt="GetBookReviews" className="w-8 h-8" />
            <span className="text-lg font-serif font-bold gradient-text">
              GetBookReviews
            </span>
          </Link>
          
          {/* User Balance & Hamburger */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg bg-dark-800 text-gray-300 hover:text-white"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar - slides from right on mobile */}
      <aside
        className={`fixed inset-y-0 z-40 w-64 bg-dark-900/95 backdrop-blur-sm border-dark-700 transform transition-transform duration-300 flex flex-col
          lg:left-0 lg:translate-x-0 lg:border-r lg:top-0
          right-0 border-l top-16 ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo - only on desktop */}
        <div className="hidden lg:block p-6 border-b border-dark-700">
          <Link to="/dashboard" className="flex items-center justify-center gap-2">
            <img src="/favicon.png" alt="GetBookReviews" className="w-10 h-10" />
            <span className="text-xl font-serif font-bold gradient-text">
              GetBookReviews
            </span>
          </Link>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-dark-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-gold-500 flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="text-gold-400">💰</span>
                  {user?.credits?.toLocaleString()}
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-primary-400">💎</span>
                  {user?.gems}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-primary-600/20 text-primary-400 border border-primary-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-dark-800'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}

          {/* Admin Panel - only for admins/moderators */}
          {(user?.role === 'admin' || user?.role === 'moderator') && (
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all mt-4 border ${
                location.pathname.startsWith('/admin')
                  ? 'bg-red-600/20 text-red-400 border-red-500/30'
                  : 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10 border-red-500/20'
              }`}
            >
              <ShieldCheckIcon className="w-5 h-5" />
              <span className="font-medium">Admin Panel</span>
              {pendingCount > 0 && (
                <span className="ml-auto bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </Link>
          )}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-dark-700 mt-auto">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}
